import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { ArrowLeft, Search, Plus, ShoppingCart, Upload, Info, Minus, X, Edit, Trash2, Check, Mail, Download, Eye, Lock, FileText } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { formatCurrency } from "../../tools/tool";
import { adminServices } from "../../services/AdminServices";
import { toast } from "react-toastify";

const OrderInformation = ({ orderData, onClose, onSubmit }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [regularItems, setRegularItems] = useState([]);
  const [specialItems, setSpecialItems] = useState([]);
  const [processedBy, setProcessedBy] = useState("Employee Name");
  const [reasonForPurchase, setReasonForPurchase] = useState("");
  const [remarks, setRemarks] = useState("");
  const [remarksFromBuCor, setRemarksFromBuCor] = useState("");
  const [pdlNumber, setPdlNumber] = useState("1234567890");
  const [accountType, setAccountType] = useState("Merchant Account");
  const [name, setName] = useState("Juan Dela Cruz");
  const [location, setLocation] = useState("MAX-GATE-01");
  const [storeCode, setStoreCode] = useState("STR-001");
  const [abstractApprovalComment, setAbstractApprovalComment] = useState("");
  const [purchaseOrderApprovalComment, setPurchaseOrderApprovalComment] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState("Select");
  const [deliveryETA, setDeliveryETA] = useState("");
  const [supplierRecommendationRemarks, setSupplierRecommendationRemarks] = useState("");
  const [remarksForMerchant, setRemarksForMerchant] = useState("");
  const [activityLogs] = useState([
    { timestamp: "2026-01-06 08:30", action: "Purchase Order (User Name made an updated on PO-26-01001)" },
    { timestamp: "2026-01-06 08:25", action: "Purchase Order (User Name created a draft Purchase Order for Supplier Name.)" },
    { timestamp: "2026-01-06 08:20", action: "Request for Quotation (User Name generated a Request for Quotation for Supplier Name.)" },
    { timestamp: "2026-01-06 08:15", action: "Abstract of Quotation (Name update on Abstract of Quotation.)" },
    { timestamp: "2026-01-06 08:10", action: "Order Created (OS-2025-001 submitted by Merchant Name.)" }
  ]);
  const [viewModeItems, setViewModeItems] = useState([
    {
      id: 1,
      description: "SKU-001 Lechon",
      quantity: 10,
      supplier: "-",
      unitPrice: 1000,
      total: 5000
    },
    {
      id: 2,
      description: "SKU-001 Lechon",
      quantity: 10,
      supplier: "-",
      unitPrice: 1000,
      total: 5000
    },
    {
      id: 3,
      description: "SKU-001 Lechon",
      quantity: 10,
      supplier: "-",
      unitPrice: 1000,
      total: 5000
    }
  ]);

  // Sample items for regular orders view mode
  const [regularViewModeItems] = useState([
    {
      id: 1,
      description: "SKU-001 Shampoo 5ML",
      uom: "Box",
      quantity: 10,
      supplier1: { costPrice: 120, total: 1200, selected: true },
      supplier2: { costPrice: 126, total: 1260, selected: false },
      supplier3: { costPrice: 127, total: 1270, selected: false }
    },
    {
      id: 2,
      description: "SKU-001 Coffee 3 in 1",
      uom: "Box",
      quantity: 10,
      supplier1: { costPrice: 120, total: 1200, selected: true },
      supplier2: { costPrice: 130, total: 1300, selected: false },
      supplier3: { costPrice: 120, total: 1200, selected: false }
    },
    {
      id: 3,
      description: "SKU-001 Rice 1KG",
      uom: "Box",
      quantity: 10,
      supplier1: { costPrice: 500, total: 5000, selected: true },
      supplier2: { costPrice: 130, total: 1300, selected: false },
      supplier3: { costPrice: 120, total: 1200, selected: false }
    }
  ]);

  // Calculate grand totals correctly
  const calculateGrandTotal = (supplierNumber) => {
    return regularViewModeItems.reduce((sum, item) => {
      const supplier = item[`supplier${supplierNumber}`];
      return sum + (supplier?.total || 0);
    }, 0);
  };
  
  const isSpecialOrder = orderData?.orderType === "Special";
  const isRegularOrder = orderData?.orderType === "Regular";
  const isViewMode = orderData?.isViewMode === true;

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    if (orderData?.pdlNumber) setPdlNumber(orderData.pdlNumber);
    if (orderData?.accountType) setAccountType(orderData.accountType);
    if (orderData?.name) setName(orderData.name);
    if (orderData?.location) setLocation(orderData.location);
    if (orderData?.reasonForPurchase) setReasonForPurchase(orderData.reasonForPurchase);
    if (orderData?.storeCode) setStoreCode(orderData.storeCode);
  }, [orderData?.pdlNumber, orderData?.accountType, orderData?.name, orderData?.location, orderData?.reasonForPurchase, orderData?.storeCode]);

  // Fetch products from api/products/data
  useEffect(() => {
    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const response = await adminServices.getData("products/data");
        if (response?.data && Array.isArray(response.data)) {
          const mapped = response.data.map((product, index) => ({
            id: product.id ?? index + 1,
            sku: product.sku || "-",
            name: product.description || product.name || "-",
            brand: product.brand || "-",
            price: product.marketPrice != null ? `P${Number(product.marketPrice).toLocaleString()}` : "P0"
          }));
          setProducts(mapped);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter products by search query
  const filteredProducts = products.filter(
    (item) =>
      !searchQuery.trim() ||
      [item.sku, item.name, item.brand].some(
        (f) => f && String(f).toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
  );

  // Product list DataTable columns (same style as ItemMaster)
  const productListColumns = [
    { name: "SKU", selector: (row) => row.sku, sortable: true, minWidth: "120px" },
    { name: "Item Description", selector: (row) => row.name, sortable: true, minWidth: "200px" },
    { name: "Brand", selector: (row) => row.brand, sortable: true, minWidth: "120px" },
    { name: "Price", selector: (row) => row.price, sortable: true, minWidth: "100px" },
    {
      name: "",
      cell: (row) => (
        <button
          className="order-information-add-item-btn"
          onClick={() => handleAddItem(row)}
          type="button"
        >
          <Plus size={18} />
        </button>
      ),
      width: "60px",
      sortable: false
    }
  ];

  const productListCustomStyles = {
    headRow: {
      style: { backgroundColor: "#002347", color: "#ffffff", fontSize: "0.875rem", fontWeight: "600" }
    },
    headCells: {
      style: {
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: "600",
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
        wordWrap: "break-word"
      }
    },
    cells: {
      style: { fontSize: "0.875rem", color: "#111827" }
    }
  };

  const handleAddItem = (item) => {
    if (isSpecialOrder) {
      // Check if item already exists in special items
      const existingItemIndex = specialItems.findIndex(i => i.sku === item.sku && i.name === item.name);
      if (existingItemIndex >= 0) {
        // Increment quantity if item exists
        const updatedItems = [...specialItems];
        updatedItems[existingItemIndex].quantity += 1;
        setSpecialItems(updatedItems);
      } else {
        // Add new item with quantity 1 and minOrderQty 1
        setSpecialItems([...specialItems, { ...item, quantity: 1, minOrderQty: 1 }]);
      }
    } else {
      // Check if item already exists in regular items
      const existingItemIndex = regularItems.findIndex(i => i.sku === item.sku && i.name === item.name);
      if (existingItemIndex >= 0) {
        // Increment quantity if item exists
        const updatedItems = [...regularItems];
        updatedItems[existingItemIndex].quantity += 1;
        setRegularItems(updatedItems);
      } else {
        // Add new item with quantity 1 and minOrderQty 1
        setRegularItems([...regularItems, { ...item, quantity: 1, minOrderQty: 1 }]);
      }
    }
  };

  const handleQuantityChange = (index, change, isSpecial = false) => {
    if (isSpecial) {
      const updatedItems = [...specialItems];
      updatedItems[index].quantity = Math.max(updatedItems[index].minOrderQty, updatedItems[index].quantity + change);
      setSpecialItems(updatedItems);
    } else {
      const item = regularItems[index];
      if (!item) return;
      if (item.quantity === 1 && change === -1) {
        setRegularItems(regularItems.filter((_, i) => i !== index));
      } else {
        const updatedItems = [...regularItems];
        updatedItems[index].quantity = Math.max(updatedItems[index].minOrderQty, updatedItems[index].quantity + change);
        setRegularItems(updatedItems);
      }
    }
  };

  const calculateTotal = () => {
    const regularTotal = regularItems.reduce((total, item) => {
      const price = parseFloat(item.price.replace('P', '').replace(',', '').trim()) || 0;
      return total + (price * item.quantity);
    }, 0);
    const specialTotal = specialItems.reduce((total, item) => {
      const price = parseFloat(item.price.replace('P', '').replace(',', '').trim()) || 0;
      return total + (price * item.quantity);
    }, 0);
    return regularTotal + specialTotal;
  };

  const formatPrice = (price) => {
    return `P${price.toLocaleString()}`;
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({
        orderData,
        regularItems: isSpecialOrder ? [] : regularItems,
        specialItems: isSpecialOrder ? specialItems : [],
        reasonForPurchase,
        processedBy,
        pdlNumber,
        accountType,
        category: orderData?.category ?? "Market"
      });
    }
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const [savingViewAction, setSavingViewAction] = useState(false);

  const handleSaveDeliveryInfo = async () => {
    const orderId = orderData?.orderId ?? orderData?.id;
    const purchaseOrderId = orderData?.purchaseOrderId ?? orderData?.purchase_order_id;
    if (!orderId || !purchaseOrderId || deliveryStatus === "Select" || !deliveryETA) {
      toast.warning("Please select a PO and enter delivery status/ETA");
      return;
    }
    try {
      setSavingViewAction(true);
      await adminServices.storeProcedure2('updateDeliveryEta', {
        orderId: Number(orderId),
        purchaseOrderId: Number(purchaseOrderId),
        deliveryEta: deliveryETA
      });
      toast.success("Delivery info saved");
    } catch (error) {
      console.error("Failed to save delivery info:", error);
      toast.error(error.response?.data?.message || "Failed to save delivery info");
    } finally {
      setSavingViewAction(false);
    }
  };

  const handleSaveChangesFromView = async () => {
    const quotationRequestId = orderData?.purchaseQuotationId ?? orderData?.quotationRequestId;
    if (!quotationRequestId) {
      toast.warning("Quotation request not available in this view");
      return;
    }
    try {
      setSavingViewAction(true);
      await adminServices.updateQuotationRequestItems(quotationRequestId, {
        supplierRecommendationRemarks: supplierRecommendationRemarks,
        items: []
      });
      toast.success("Changes saved");
    } catch (error) {
      console.error("Failed to save changes:", error);
      toast.error(error.response?.data?.message || "Failed to save changes");
    } finally {
      setSavingViewAction(false);
    }
  };

  const handleGenerateRFQFromView = async () => {
    const quotationRequestId = orderData?.purchaseQuotationId ?? orderData?.quotationRequestId;
    const supplierId = orderData?.supplierId ?? orderData?.selectedSupplierId;
    if (!quotationRequestId) {
      toast.warning("Quotation request not available");
      return;
    }
    try {
      setSavingViewAction(true);
      await adminServices.generateRFQPdf(quotationRequestId, { supplierId: Number(supplierId) || 0 });
      toast.success("RFQ generated");
    } catch (error) {
      console.error("Failed to generate RFQ:", error);
      toast.error(error.response?.data?.message || "Failed to generate RFQ");
    } finally {
      setSavingViewAction(false);
    }
  };

  const handleFinalizeAbstractFromView = async () => {
    const quotationRequestId = orderData?.purchaseQuotationId ?? orderData?.quotationRequestId;
    const prNo = orderData?.prNo;
    if (!quotationRequestId) {
      toast.warning("Quotation request not available");
      return;
    }
    try {
      setSavingViewAction(true);
      await adminServices.approveQuotationRequest({
        purchaseQuotationId: quotationRequestId,
        prNo: prNo || "",
        status: "APPROVED",
        approvalRemarks: abstractApprovalComment || ""
      });
      toast.success("Abstract of Quotation finalized");
    } catch (error) {
      console.error("Failed to finalize abstract:", error);
      toast.error(error.response?.data?.message || "Failed to finalize abstract");
    } finally {
      setSavingViewAction(false);
    }
  };

  const handleCreateDraftFromView = async (section) => {
    const orderId = orderData?.orderId ?? orderData?.id;
    if (!orderId) {
      toast.warning("Order ID not available");
      return;
    }
    if (section === "Purchase Order") {
      try {
        setSavingViewAction(true);
        await adminServices.createPurchaseOrderDraft({
          purchaseOrderId: 0,
          ipxOrderId: Number(orderId),
          purchaserId: orderData?.approvedBy || 0,
          deliveryEta: new Date().toISOString(),
          paymentTerms: orderData?.paymentTerms || "",
          isDraft: true,
          poRemarks: supplierRecommendationRemarks || "",
          items: orderData?.items || []
        });
        toast.success("Purchase Order draft created");
      } catch (error) {
        console.error("Failed to create PO draft:", error);
        toast.error(error.response?.data?.message || "Failed to create PO draft");
      } finally {
        setSavingViewAction(false);
      }
      return;
    }
    toast.info(`Create ${section} is available from Process Order view`);
  };

  if (!orderData) return null;

  // View mode for special and regular orders
  if (isViewMode && (isSpecialOrder || isRegularOrder)) {
    return (
      <div className="dashboard-content">
        <div className="order-information-view-page">
          {/* Two Column Layout */}
          <div className="order-information-view-layout">
            {/* Left Column */}
            <div className="order-information-view-left-column">
              {/* Back Link */}
              <button className="order-information-view-back-link" onClick={onClose}>
                <ArrowLeft size={18} />
                Back to Order Management
              </button>

              {/* Order ID and Status */}
              <div className="order-information-view-header-section">
                <div className="order-information-view-header-top">
                  <div className="order-information-view-order-id">{orderData?.orderId ?? orderData?.osNumber ?? "OS-2025-001"}</div>
                  <div className="order-information-view-status-badge processing">{orderData?.orderStatus || "Processing"}</div>
                </div>
                
                {/* Merchant Reserved Funds */}
                <div className="order-information-view-merchant-funds">
                  <div className="order-information-view-merchant-label">
                    Merchant Reserved Funds
                    <Info size={14} className="order-information-info-icon" />
                  </div>
                  <div className="order-information-view-merchant-value">{formatCurrency(orderData?.reservedFunds ?? orderData?.merchantReservedFunds ?? 0)}</div>
                </div>
              </div>

              {/* Order Information */}
              <div className="order-information-view-section-card">
                <h2 className="order-information-view-section-title">Order Information</h2>
                <div className="order-information-view-info-grid">
                  <div className="order-information-view-info-field">
                    <label className="order-information-view-info-label">Order Type:</label>
                    <span className="order-information-view-info-value">{orderData?.orderType || (isSpecialOrder ? "Special Order" : "Regular Order")}</span>
                  </div>
                  <div className="order-information-view-info-field">
                    <label className="order-information-view-info-label">Name:</label>
                    <span className="order-information-view-info-value">{name}</span>
                  </div>
                  <div className="order-information-view-info-field">
                    <label className="order-information-view-info-label">Location:</label>
                    <span className="order-information-view-info-value">{location}</span>
                  </div>
                  <div className="order-information-view-info-field">
                    <label className="order-information-view-info-label">Account Type:</label>
                    <span className="order-information-view-info-value">{accountType}</span>
                  </div>
                  <div className="order-information-view-info-field">
                    <label className="order-information-view-info-label">PDL Number:</label>
                    <span className="order-information-view-info-value">{pdlNumber}</span>
                  </div>
                  <div className="order-information-view-info-field">
                    <label className="order-information-view-info-label">Store Code:</label>
                    <span className="order-information-view-info-value">{storeCode}</span>
                  </div>
                </div>
              </div>

              {/* Manager Approval */}
              <div className="order-information-view-section-card">
                <h2 className="order-information-view-section-title">Manager Approval</h2>
                
                {/* Abstract of Quotation Approval */}
                <div className="order-information-view-approval-item">
                  <div className="order-information-view-approval-header">
                    <label className="order-information-view-approval-label">Abstract of Quotation</label>
                    <span className={`order-information-view-approval-badge ${String(orderData?.abstractStatus || "PENDING").toLowerCase()}`}>
                      {orderData?.abstractStatus || "PENDING"}
                    </span>
                  </div>
                  <textarea
                    className="order-information-view-approval-textarea"
                    value={abstractApprovalComment}
                    onChange={(e) => setAbstractApprovalComment(e.target.value)}
                    placeholder=""
                    rows={3}
                  />
                </div>

                {/* Purchase Order Approval */}
                <div className="order-information-view-approval-item">
                  <div className="order-information-view-approval-header">
                    <label className="order-information-view-approval-label">Purchase Order</label>
                    <span className={`order-information-view-approval-badge ${String(orderData?.poStatus || orderData?.purchaseOrderStatus || "PENDING").toLowerCase()}`}>
                      {orderData?.poStatus || orderData?.purchaseOrderStatus || "PENDING"}
                    </span>
                  </div>
                  <div className="order-information-view-approval-supplier">{orderData?.supplierName || orderData?.supplier_name || "-"}</div>
                  <textarea
                    className="order-information-view-approval-textarea"
                    value={purchaseOrderApprovalComment}
                    onChange={(e) => setPurchaseOrderApprovalComment(e.target.value)}
                    placeholder=""
                    rows={3}
                  />
                </div>
              </div>

              {/* Delivery Info */}
              <div className="order-information-view-section-card">
                <h2 className="order-information-view-section-title">Delivery Info</h2>
                <div className="order-information-view-delivery-grid">
                  <div className="order-information-view-delivery-field">
                    <label className="order-information-view-delivery-label">Delivery Status</label>
                    <select
                      className="order-information-view-delivery-select"
                      value={deliveryStatus}
                      onChange={(e) => setDeliveryStatus(e.target.value)}
                    >
                      <option value="Select">Select</option>
                      <option value="Pending">Pending</option>
                      <option value="In Transit">In Transit</option>
                      <option value="Delivered">Delivered</option>
                    </select>
                  </div>
                  <div className="order-information-view-delivery-field">
                    <label className="order-information-view-delivery-label">Delivery ETA</label>
                    <input
                      type="text"
                      className="order-information-view-delivery-input"
                      value={deliveryETA}
                      onChange={(e) => setDeliveryETA(e.target.value)}
                      placeholder="MM/DD/YY"
                    />
                  </div>
                </div>
                <div className="order-information-view-delivery-buttons">
                  <button className="order-information-view-save-delivery-btn" onClick={handleSaveDeliveryInfo} disabled={savingViewAction}>
                    {savingViewAction ? "Saving..." : "Save"}
                  </button>
                  <button className="order-information-view-cancel-delivery-btn" onClick={() => { setDeliveryStatus("Select"); setDeliveryETA(""); }}>
                    Cancel
                  </button>
                </div>
              </div>

              {/* Activity Logs */}
              <div className="order-information-view-section-card">
                <h2 className="order-information-view-section-title">Activity Logs</h2>
                <div className="order-information-view-activity-logs">
                  {activityLogs.map((log, index) => (
                    <div key={index} className="order-information-view-activity-item">
                      <span className="order-information-view-activity-time">{log.timestamp}</span>
                      <span className="order-information-view-activity-action">{log.action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="order-information-view-right-column">

              {/* Abstract of Quotation Section */}
              <div className="order-information-view-section-card">
                <div className="order-information-view-abstract-header">
                  <h2 className="order-information-view-section-title">Abstract of Quotation</h2>
                  <button className="order-information-view-select-supplier-btn">
                    Select New Supplier
                  </button>
                </div>
            <div className="order-information-view-table-container">
              {isRegularOrder ? (
                <table className="order-information-view-table order-information-view-table-regular">
                  <thead>
                    <tr>
                      <th rowSpan="2" className="order-information-view-th-checkbox">
                        <div className="order-information-view-checkbox-header">
                          <input 
                            type="checkbox" 
                            onChange={(e) => {
                              // Handle select all
                            }}
                          />
                          <span>Item</span>
                        </div>
                      </th>
                      <th rowSpan="2">UOM</th>
                      <th rowSpan="2">QTY</th>
                      <th colSpan="2">Supplier 1</th>
                      <th colSpan="2">Supplier 2</th>
                      <th colSpan="2">Supplier 3</th>
                      <th rowSpan="2">Actions</th>
                    </tr>
                    <tr className="order-information-view-sub-header">
                      <th>Cost Price</th>
                      <th>Total</th>
                      <th>Cost Price</th>
                      <th>Total</th>
                      <th>Cost Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regularViewModeItems.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          <div className="order-information-view-item-description">
                            <input 
                              type="checkbox" 
                              onChange={(e) => {
                                // Handle checkbox change
                              }}
                            />
                            <span>{item.description}</span>
                          </div>
                        </td>
                        <td>{item.uom}</td>
                        <td>{item.quantity}</td>
                        <td className={item.supplier1?.selected ? "order-information-view-selected" : ""}>
                          P{item.supplier1.costPrice.toLocaleString()}
                        </td>
                        <td className={item.supplier1?.selected ? "order-information-view-selected" : ""}>
                          <div className="order-information-view-total-cell">
                            P{item.supplier1.total.toLocaleString()}
                            {item.supplier1.selected ? (
                              <div className="order-information-view-checkmark">
                                <Check size={12} />
                              </div>
                            ) : (
                              <input type="checkbox" className="order-information-view-total-checkbox" />
                            )}
                          </div>
                        </td>
                        <td className={item.supplier2?.selected ? "order-information-view-selected" : ""}>
                          P{item.supplier2.costPrice.toLocaleString()}
                        </td>
                        <td className={item.supplier2?.selected ? "order-information-view-selected" : ""}>
                          <div className="order-information-view-total-cell">
                            P{item.supplier2.total.toLocaleString()}
                            {item.supplier2.selected ? (
                              <div className="order-information-view-checkmark">
                                <Check size={12} />
                              </div>
                            ) : (
                              <input type="checkbox" className="order-information-view-total-checkbox" />
                            )}
                          </div>
                        </td>
                        <td className={item.supplier3?.selected ? "order-information-view-selected" : ""}>
                          P{item.supplier3.costPrice.toLocaleString()}
                        </td>
                        <td className={item.supplier3?.selected ? "order-information-view-selected" : ""}>
                          <div className="order-information-view-total-cell">
                            P{item.supplier3.total.toLocaleString()}
                            {item.supplier3.selected ? (
                              <div className="order-information-view-checkmark">
                                <Check size={12} />
                              </div>
                            ) : (
                              <input type="checkbox" className="order-information-view-total-checkbox" />
                            )}
                          </div>
                        </td>
                        <td>
                          <button className="order-information-view-delete-btn">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="order-information-view-grand-total">
                      <td colSpan="3"><strong>GRAND TOTAL</strong></td>
                      <td></td>
                      <td><strong>{formatPrice(calculateGrandTotal(1))}</strong></td>
                      <td></td>
                      <td><strong>{formatPrice(calculateGrandTotal(2))}</strong></td>
                      <td></td>
                      <td><strong>{formatPrice(calculateGrandTotal(3))}</strong></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <table className="order-information-view-table">
                  <thead>
                    <tr>
                      <th>Item Description</th>
                      <th>QTY</th>
                      <th>Supplier</th>
                      <th>Unit Price</th>
                      <th>Total</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewModeItems.map((item, index) => (
                      <tr key={item.id}>
                        <td>{item.description}</td>
                        <td>
                          <div className="order-information-view-quantity-control">
                            <button
                              className="order-information-view-quantity-btn"
                              onClick={() => {
                                const updated = [...viewModeItems];
                                updated[index].quantity = Math.max(1, updated[index].quantity - 1);
                                updated[index].total = updated[index].quantity * updated[index].unitPrice;
                                setViewModeItems(updated);
                              }}
                            >
                              <Minus size={14} />
                            </button>
                            <input
                              type="number"
                              className="order-information-view-quantity-input"
                              value={item.quantity}
                              readOnly
                            />
                            <button
                              className="order-information-view-quantity-btn"
                              onClick={() => {
                                const updated = [...viewModeItems];
                                updated[index].quantity += 1;
                                updated[index].total = updated[index].quantity * updated[index].unitPrice;
                                setViewModeItems(updated);
                              }}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="order-information-view-supplier-input"
                            value={item.supplier}
                            readOnly
                          />
                        </td>
                        <td>₱{item.unitPrice.toLocaleString()}</td>
                        <td>₱{item.total.toLocaleString()}</td>
                        <td>
                          <div className="order-information-view-actions">
                            <button className="order-information-view-action-btn">
                              <Edit size={16} />
                            </button>
                            <button className="order-information-view-action-btn">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

                {/* Table Section */}
                <div className="order-information-view-table-container">
                  {isRegularOrder ? (
                    <table className="order-information-view-table order-information-view-table-regular">
                      <thead>
                        <tr>
                          <th rowSpan="2" className="order-information-view-th-checkbox">
                            <div className="order-information-view-checkbox-header">
                              <input 
                                type="checkbox" 
                                onChange={(e) => {
                                  // Handle select all
                                }}
                              />
                              <span>Item Description</span>
                            </div>
                          </th>
                          <th rowSpan="2">UOM</th>
                          <th rowSpan="2">QTY</th>
                          <th colSpan="2">Supplier 1</th>
                          <th colSpan="2">Supplier 2</th>
                          <th colSpan="2">Supplier 3</th>
                        </tr>
                        <tr className="order-information-view-sub-header">
                          <th>Cost Price</th>
                          <th>Total</th>
                          <th>Cost Price</th>
                          <th>Total</th>
                          <th>Cost Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {regularViewModeItems.map((item, index) => (
                          <tr key={item.id}>
                            <td>
                              <div className="order-information-view-item-description">
                                <input 
                                  type="checkbox" 
                                  onChange={(e) => {
                                    // Handle checkbox change
                                  }}
                                />
                                <span>{item.description}</span>
                              </div>
                            </td>
                            <td>{item.uom}</td>
                            <td>{item.quantity}</td>
                            <td className={item.supplier1?.selected ? "order-information-view-selected" : ""}>
                              P{item.supplier1.costPrice.toLocaleString()}
                            </td>
                            <td className={item.supplier1?.selected ? "order-information-view-selected" : ""}>
                              <div className="order-information-view-total-cell">
                                P{item.supplier1.total.toLocaleString()}
                                {item.supplier1.selected ? (
                                  <div className="order-information-view-checkmark">
                                    <Check size={12} />
                                  </div>
                                ) : (
                                  <input type="checkbox" className="order-information-view-total-checkbox" />
                                )}
                              </div>
                            </td>
                            <td className={item.supplier2?.selected ? "order-information-view-selected" : ""}>
                              P{item.supplier2.costPrice.toLocaleString()}
                            </td>
                            <td className={item.supplier2?.selected ? "order-information-view-selected" : ""}>
                              <div className="order-information-view-total-cell">
                                P{item.supplier2.total.toLocaleString()}
                                {item.supplier2.selected ? (
                                  <div className="order-information-view-checkmark">
                                    <Check size={12} />
                                  </div>
                                ) : (
                                  <input type="checkbox" className="order-information-view-total-checkbox" />
                                )}
                              </div>
                            </td>
                            <td className={item.supplier3?.selected ? "order-information-view-selected" : ""}>
                              P{item.supplier3.costPrice.toLocaleString()}
                            </td>
                            <td className={item.supplier3?.selected ? "order-information-view-selected" : ""}>
                              <div className="order-information-view-total-cell">
                                P{item.supplier3.total.toLocaleString()}
                                {item.supplier3.selected ? (
                                  <div className="order-information-view-checkmark">
                                    <Check size={12} />
                                  </div>
                                ) : (
                                  <input type="checkbox" className="order-information-view-total-checkbox" />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        <tr className="order-information-view-grand-total">
                          <td colSpan="3"><strong>GRAND TOTAL</strong></td>
                          <td></td>
                          <td><strong>{formatPrice(calculateGrandTotal(1))}</strong></td>
                          <td></td>
                          <td><strong>{formatPrice(calculateGrandTotal(2))}</strong></td>
                          <td></td>
                          <td><strong>{formatPrice(calculateGrandTotal(3))}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <table className="order-information-view-table">
                      <thead>
                        <tr>
                          <th>Item Description</th>
                          <th>QTY</th>
                          <th>Supplier</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewModeItems.map((item, index) => (
                          <tr key={item.id}>
                            <td>{item.description}</td>
                            <td>{item.quantity}</td>
                            <td>{item.supplier}</td>
                            <td>₱{item.unitPrice.toLocaleString()}</td>
                            <td>₱{item.total.toLocaleString()}</td>
                            <td>
                              <button className="order-information-view-action-btn">
                                <Edit size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Action Buttons Below Table */}
                <div className="order-information-view-abstract-buttons">
                  <button className="order-information-view-save-changes-btn" onClick={handleSaveChangesFromView} disabled={savingViewAction}>
                    {savingViewAction ? "Saving..." : "Save Changes"}
                  </button>
                  <button className="order-information-view-generate-rfq-btn" onClick={handleGenerateRFQFromView} disabled={savingViewAction}>
                    Generate RFQ
                  </button>
                </div>

                {/* Supplier Recommendation Remarks */}
                <div className="order-information-view-section-card">
                  <label className="order-information-view-section-title">Supplier Recommendation Remarks</label>
                  <textarea
                    className="order-information-view-recommendation-textarea"
                    value={supplierRecommendationRemarks}
                    onChange={(e) => setSupplierRecommendationRemarks(e.target.value)}
                    placeholder=""
                    rows={4}
                  />
                </div>

                {/* Finalize Button */}
                <button className="order-information-view-finalize-btn" onClick={handleFinalizeAbstractFromView} disabled={savingViewAction}>
                  {savingViewAction ? "Finalizing..." : "Finalize Abstract of Quotation"}
                </button>

                {/* Request for Quotation */}
                <div className="order-information-view-section-card">
                  <h2 className="order-information-view-section-title">Request for Quotation</h2>
                  <div className="order-information-view-rfq-item">
                    <FileText size={18} className="order-information-view-rfq-icon" />
                    <span className="order-information-view-rfq-text">RequestForQ... RequestForQuotation_Supplier1.pdf</span>
                    <div className="order-information-view-rfq-actions">
                      <button className="order-information-view-rfq-action-btn"><Mail size={16} /></button>
                      <button className="order-information-view-rfq-action-btn"><Download size={16} /></button>
                      <button className="order-information-view-rfq-action-btn"><Eye size={16} /></button>
                      <button className="order-information-view-rfq-action-btn"><Lock size={16} /></button>
                    </div>
                  </div>
                </div>

                {/* Purchase Order */}
                <div className="order-information-view-section-card">
                  <div className="order-information-view-document-header">
                    <h2 className="order-information-view-section-title">Purchase Order</h2>
                    <button className="order-information-view-create-draft-btn" onClick={() => handleCreateDraftFromView("Purchase Order")} disabled={savingViewAction}>
                      Create Draft
                    </button>
                  </div>
                  <p className="order-information-view-no-document">{orderData?.purchaseOrderNo ? orderData.purchaseOrderNo : "No Purchase Order created yet"}</p>
                </div>

                {/* Delivery Note */}
                <div className="order-information-view-section-card">
                  <div className="order-information-view-document-header">
                    <h2 className="order-information-view-section-title">Delivery Note</h2>
                    <button className="order-information-view-create-draft-btn" onClick={() => handleCreateDraftFromView("Delivery Note")} disabled={savingViewAction}>
                      Create Draft
                    </button>
                  </div>
                  <p className="order-information-view-no-document">{orderData?.deliveryNoteNo ? orderData.deliveryNoteNo : "No Delivery Note created yet"}</p>
                </div>

                {/* Sales Order */}
                <div className="order-information-view-section-card">
                  <div className="order-information-view-document-header">
                    <h2 className="order-information-view-section-title">Sales Order</h2>
                    <button className="order-information-view-create-draft-btn" onClick={() => handleCreateDraftFromView("Sales Order")} disabled={savingViewAction}>
                      Create Draft
                    </button>
                  </div>
                  <p className="order-information-view-no-document">{orderData?.salesOrderNo ? orderData.salesOrderNo : "No Sales Order created yet"}</p>
                </div>

                {/* Remarks for Merchant */}
                <div className="order-information-view-section-card">
                  <label className="order-information-view-section-title">Remarks for Merchant</label>
                  <textarea
                    className="order-information-view-merchant-textarea"
                    value={remarksForMerchant}
                    onChange={(e) => setRemarksForMerchant(e.target.value)}
                    placeholder=""
                    rows={4}
                  />
                </div>

                {/* Bottom Action Buttons */}
                <div className="order-information-view-bottom-buttons">
                  <button className="order-information-view-bottom-save-btn">Save Changes</button>
                  <button className="order-information-view-bottom-cancel-btn" onClick={onClose}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
    );
  }

  // Create mode (existing code)
  return (
    <div className="dashboard-content">
      <div className="order-information-page">
        {/* Header */}
        <div className="order-information-header">
          <button className="order-information-back-btn" onClick={onClose}>
            <ArrowLeft size={20} />
            Back to Order Management
          </button>
          <button className="order-information-export-btn">
            <Upload size={18} />
            Export to PDF
          </button>
        </div>

        {/* Order Information Section - populated from New Order Modal */}
        <div className="order-information-section">
          <h2 className="order-information-section-title">Order Information</h2>
          <div className="order-information-grid">
            <div className="order-information-field">
              <label className="order-information-label">Order ID</label>
              <div className="order-information-value">{orderData?.orderId ?? orderData?.osNumber ?? "-"}</div>
            </div>
            <div className="order-information-field">
              <label className="order-information-label">Date Issued</label>
              <div className="order-information-value">{orderData?.dateIssued ? formatDate(orderData.dateIssued) : "-"}</div>
            </div>
            <div className="order-information-field">
              <label className="order-information-label">Order Type</label>
              <div className="order-information-value">{orderData?.orderType === "Special" ? "Special Order" : orderData?.orderType === "Regular" ? "Regular Order" : (orderData?.orderType ?? "-")}</div>
            </div>
            <div className="order-information-field">
              <label className="order-information-label">Name</label>
              <div className="order-information-value">{(name || orderData?.name) ?? "-"}</div>
            </div>
            <div className="order-information-field">
              <label className="order-information-label">PDL Number</label>
              <div className="order-information-value">{(pdlNumber || orderData?.pdlNumber) ?? "-"}</div>
            </div>
            <div className="order-information-field">
              <label className="order-information-label">Account Type</label>
              <div className="order-information-value">{(accountType || orderData?.accountType) ?? "-"}</div>
            </div>
            {isRegularOrder && (
              <div className="order-information-field">
                <label className="order-information-label">Category</label>
                <div className="order-information-value">{orderData?.category ?? "-"}</div>
              </div>
            )}
            <div className="order-information-field">
              <label className="order-information-label">Location</label>
              <div className="order-information-value">{(location || orderData?.location) ?? "-"}</div>
            </div>
            <div className="order-information-field">
              <label className="order-information-label">Store Code</label>
              <div className="order-information-value">{(storeCode || orderData?.storeCode) ?? "-"}</div>
            </div>
            {isSpecialOrder && (
              <div className="order-information-field order-information-field-full">
                <label className="order-information-label">Reason for Purchase</label>
                <input
                  type="text"
                  className="order-information-input"
                  value={reasonForPurchase}
                  onChange={(e) => setReasonForPurchase(e.target.value)}
                  placeholder="state reason here..."
                />
              </div>
            )}
          </div>
        </div>

        {/* Merchant Wallet Balance */}
        <div className="order-information-wallet-section">
          <div className="order-information-wallet-header">
            <label className="order-information-wallet-label">Merchant Wallet Balance</label>
            <Info size={16} className="order-information-info-icon" />
          </div>
          <div className="order-information-wallet-value">{formatCurrency(orderData?.walletBalance ?? 0)}</div>
        </div>

        {/* Item Search Section */}
        <div className="order-information-search-section">
          <div className="order-information-search-container">
            <Search className="order-information-search-icon" size={18} />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="order-information-search-input"
            />
            <button className="order-information-search-btn">Search</button>
          </div>

          <div className="order-information-items-list order-information-products-datatable">
            {productsLoading ? (
              <div className="order-information-items-loading">Loading products...</div>
            ) : (
              <DataTable
                columns={productListColumns}
                data={filteredProducts}
                customStyles={productListCustomStyles}
                pagination={true}
                paginationPerPage={10}
                paginationRowsPerPageOptions={[]}
                paginationComponentOptions={{
                  rowsPerPageText: "",
                  rangeSeparatorText: "of",
                  noRowsPerPage: true,
                  selectAllRowsItem: false
                }}
                highlightOnHover
                noDataComponent={
                  <div style={{ padding: "40px 20px", textAlign: "center", color: "#666", fontSize: "1rem" }}>
                    No products found
                  </div>
                }
              />
            )}
          </div>
        </div>

        {/* Regular Items Section */}
        {!isSpecialOrder && (
          <div className="order-information-regular-section">
            <div className="order-information-regular-header">
              <h3 className="order-information-regular-title">Regular Items</h3>
              <div className="order-information-regular-add">
                <span className="order-information-regular-add-text">Could not find item? Add it here.</span>
                <button className="order-information-list-item-btn">List New Item</button>
              </div>
            </div>

            {regularItems.length === 0 ? (
              <div className="order-information-empty-state">
                <ShoppingCart size={48} className="order-information-empty-icon" />
                <p className="order-information-empty-text">No items added yet.</p>
                <p className="order-information-empty-subtext">Total Cost may vary upon finalization of order.</p>
              </div>
            ) : (
              <div className="order-information-items-table-container">
                <table className="order-information-items-table">
                  <thead>
                    <tr>
                      <th>SKU Code</th>
                      <th>Item Description</th>
                      <th>QTY</th>
                      <th>Min. Order QTY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regularItems.map((item, index) => (
                      <tr key={index}>
                        <td>{item.sku}</td>
                        <td>{item.name}</td>
                        <td>
                          <div className="order-information-quantity-control">
                            <button
                              className="order-information-quantity-btn"
                              onClick={() => handleQuantityChange(index, -1, false)}
                            >
                              <Minus size={16} />
                            </button>
                            <span className="order-information-quantity-value">{item.quantity}</span>
                            <button
                              className="order-information-quantity-btn"
                              onClick={() => handleQuantityChange(index, 1, false)}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </td>
                        <td>{item.minOrderQty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Special Items Section */}
        {isSpecialOrder && (
          <div className="order-information-regular-section">
            <div className="order-information-regular-header">
              <h3 className="order-information-regular-title">Special Items</h3>
              <div className="order-information-regular-add">
                <span className="order-information-regular-add-text">Could not find item? Add it here.</span>
                <button className="order-information-list-item-btn">List New Item</button>
              </div>
            </div>

            {specialItems.length === 0 ? (
              <div className="order-information-empty-state">
                <ShoppingCart size={48} className="order-information-empty-icon" />
                <p className="order-information-empty-text">No items added yet.</p>
                <p className="order-information-empty-subtext">Total Cost may vary upon finalization of order.</p>
              </div>
            ) : (
              <div className="order-information-items-table-container">
                <table className="order-information-items-table">
                  <thead>
                    <tr>
                      <th>SKU Code</th>
                      <th>Item Description</th>
                      <th>QTY</th>
                      <th>Min. Order QTY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specialItems.map((item, index) => (
                      <tr key={index}>
                        <td>{item.sku}</td>
                        <td>{item.name}</td>
                        <td>
                          <div className="order-information-quantity-control">
                            <button
                              className="order-information-quantity-btn"
                              onClick={() => handleQuantityChange(index, -1, true)}
                              disabled={item.quantity <= item.minOrderQty}
                            >
                              <Minus size={16} />
                            </button>
                            <span className="order-information-quantity-value">{item.quantity}</span>
                            <button
                              className="order-information-quantity-btn"
                              onClick={() => handleQuantityChange(index, 1, true)}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </td>
                        <td>{item.minOrderQty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Order Summary */}
        {(regularItems.length > 0 || specialItems.length > 0) && (
          <div className="order-information-summary-section">
            <div className="order-information-total">
              <span className="order-information-total-label">Total:</span>
              <span className="order-information-total-value">{formatPrice(calculateTotal())}</span>
            </div>
            <p className="order-information-disclaimer">
              Final cost may change. Please ensure your wallet balance is higher than the order total to avoid cancellation.
            </p>
          </div>
        )}

        {/* Reason for Purchase Section (Bottom) - Only for Special Orders */}
        {isSpecialOrder && (
          <div className="order-information-reason-section">
            <label className="order-information-label">Reason for Purchase</label>
            <textarea
              className="order-information-reason-textarea"
              value={reasonForPurchase}
              onChange={(e) => setReasonForPurchase(e.target.value)}
              placeholder="state reason here..."
              rows={4}
            />
          </div>
        )}

        {/* Footer Actions */}
        <div className="order-information-footer">
          <div className="order-information-processed-section">
            <label className="order-information-processed-label">Processed by</label>
            <div className="order-information-select-wrapper">
              <input
                type="text"
                className="order-information-processed-select"
                value={processedBy}
                onChange={(e) => setProcessedBy(e.target.value)}
                placeholder="Employee Name"
              />
            </div>
          </div>

          <div className="order-information-footer-actions">
            <button 
              className="order-information-cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              className="order-information-submit-btn"
              onClick={handleSubmit}
            >
              Submit Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderInformation;

